import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import posthog from 'posthog-js';
import { BehaviorSubject, map, mergeMap, tap } from 'rxjs';
import { CoreItem, CoreItemType, VertexRelation, VertexRelationAdd, VertexRelationType } from '../core-item/core-item';
import { CoreItemService } from '../core-item/core-item.service';
import { PostHogEvent } from '../posthog/posthog.event';
import { Tag } from './tag';

@Injectable({
  providedIn: 'root',
})
export class TagService {
  private _changeSubject = new BehaviorSubject<Tag | undefined>(undefined);

  constructor(private coreItemService: CoreItemService) {}

  tagList$() {
    return this._changeSubject.asObservable().pipe(
      mergeMap((_) => {
        return this.coreItemService.getCoreItemList$({ coreTypeList: [CoreItemType.tag] }).pipe(
          map((list) => {
            list.sort((a, b) => ((a.createdTime ?? DateTime.local()) < (b.createdTime ?? DateTime.local()) ? 1 : -1));
            return list as Tag[];
          })
        );
      })
    );
  }

  updateTag$(tag: Tag) {
    posthog.capture(PostHogEvent.tagUpdate);
    return this.coreItemService.updateCoreItem$(tag);
  }

  addTag$(title: string, color: string, type: string, parentId?: string) {
    posthog.capture(PostHogEvent.tagAdd);

    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        addMetadataList: [
          {
            title,
            coreType: CoreItemType.tag,
            type,
            parentId,
            subData: {
              unsafeSubData: {
                color,
              },
            },
          },
        ],
      })
      .pipe(
        map((arr) => {
          this._changeSubject.next(arr[0] as Tag);
          return arr[0] as Tag;
        })
      );
  }

  deleteTagList$(tagList: Tag[]) {
    const deleteMetadataList = tagList.map((c) => ({
      id: c.id,
      sk: c.sk,
    }));

    posthog.capture(PostHogEvent.tagDelete);

    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        deleteMetadataList,
      })
      .pipe(
        tap((_) => {
          this._changeSubject.next(undefined);
        })
      );
  }

  assignTag$(tag: Tag, coreList: CoreItem[]) {
    const addRelationList: VertexRelationAdd[] = [];

    coreList.forEach((core) => {
      addRelationList.push({
        from: core.id,
        to: tag.id,
        relation: VertexRelationType.has,
      });
    });

    return this.coreItemService.batchWriteMetadataAndRelationList$({ addRelationList });
  }

  unassignTag$(tag: Tag, coreList: CoreItem[]) {
    const deleteRelationList: VertexRelation[] = [];

    coreList.forEach((core) => {
      deleteRelationList.push({
        from: core.id,
        to: tag.id,
        relation: VertexRelationType.has,
      });
    });

    return this.coreItemService.batchWriteMetadataAndRelationList$({ deleteRelationList });
  }
}
